import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import MaterialTable from "material-table";
import { IconButton, TextField } from "@material-ui/core";
import Icon from "@jll/icon";
import Tooltip from "@material-ui/core/Tooltip";

import {
  tableStyle,
  tableOptions,
  tableIcons,
} from "../../../../Component/Table/tableCommonStyles";

import styles from "./styles";

import { formatDate } from "../../../../Utilities/Functions";
import CustomMTableActions from "../../../../Component/Table/CustomMTableActions";
import { updateEntityDocumentName } from "../../../../API/Entity";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(styles);

const FileNameEditor = ({ filename, onChange }) => {
  const [value, setValue] = useState("");
  const [ext, setExt] = useState();

  useEffect(() => {
    if (filename) {
      const idx = filename.lastIndexOf(".");
      let namePart = filename;
      let ext = "";
      if (idx > -1) {
        namePart = filename.substring(0, idx);
        ext = filename.substring(idx + 1);
      }
      setValue(namePart);
      setExt(ext);
    }
  }, []);

  const onNameChange = (name) => {
    setValue(name);
    onChange(name + "." + ext);
  };

  return (
    <TextField
      type="text"
      value={value}
      onChange={(e) => onNameChange(e.target.value)}
    />
  );
};

const DocumentsTable = ({
  data,
  category,
  hideExpired,
  onRowViewClick,
  onRowDownloadClick,
  canViewDocumentContents,
  canRemoveDocument,
  canUpdateDocumentName,
  removeDocument,
}) => {
  const classes = useStyles();
  const { showMessage, showErrorMessage } = useContext(ModalDialogContext);
  const { t } = useTranslation();

  const initiateView = (docId) => {
    if (onRowViewClick && typeof onRowViewClick === "function") {
      onRowViewClick(docId);
    }
  };

  const hasExpired = (expDate) => {
    if (!expDate) {
      return false;
    }
    const ed = new Date(expDate);
    const cd = new Date();
    cd.setHours(0, 0, 0, 0);
    return ed < cd;
  };

  return (
    <div className={classes.tableWrapper}>
      <MaterialTable
        style={tableStyle}
        icons={tableIcons}
        editable={{
          isEditable: (rowData) => canUpdateDocumentName,
          isDeletable: (rowData) => false,
          onRowUpdate: (newData, oldData) => {
            const { entity_id: entityId, id: documentId, filename } = newData;
            return new Promise(async (resolve, reject) => {
              try {
                if (
                  !filename ||
                  filename.trim().length === 0 ||
                  filename.startsWith(".")
                ) {
                  showMessage({
                    message: t("messages.record.documents.fileNameError"),
                  });
                  reject();
                  return;
                }
                await updateEntityDocumentName({
                  entityId,
                  documentId,
                  category,
                  documentName: filename,
                });
                const index = oldData.tableData.id;
                data[index].filename = filename;
                showMessage({
                  message: t("messages.record.documents.documentNameUpdated"),
                });
                resolve();
              } catch (error) {
                showErrorMessage({ error });
                reject();
              }
            });
          },
        }}
        options={tableOptions}
        isLoading={false}
        data={data.filter((d) => !hideExpired || !hasExpired(d.expiry_date))}
        localization={{
          header: {
            actions: t("labels.common.table.actions"),
          },
          body: {
            emptyDataSourceMessage: t("labels.common.table.noRecordsFound"),
          },
        }}
        columns={[
          {
            title: t("labels.record.documents.table.headers.documentType"),
            field: "type_name",
            editable: "never",
          },
          {
            title: t("labels.record.documents.table.headers.createdBy"),
            field: "created_by_user_name",
            editable: "never",
          },
          {
            title: t("labels.record.documents.table.headers.fileName"),
            field: "filename",
            editComponent: (props) => (
              <FileNameEditor
                filename={props.value}
                onChange={(name) => props.onChange(name)}
              />
            ),
          },
          {
            title: t("labels.record.documents.table.headers.expiryDate"),
            field: "expiry_date",
            cellStyle: (r, rowData) => {
              return hasExpired(rowData.expiry_date) ? { color: "red" } : "";
            },
            render: (rowData) => {
              return (
                <span>
                  {hasExpired(rowData.expiry_date)
                    ? `${formatDate(rowData.expiry_date)} ${t(
                        "labels.record.documents.table.values.expired",
                      )}`
                    : rowData.expiry_date
                    ? formatDate(rowData.expiry_date)
                    : t("labels.common.na")}
                </span>
              );
            },
            editable: "never",
          },
        ]}
        actions={[
          canViewDocumentContents && {
            icon: "view",
            position: "row",
            onClick: (event, rowData) => initiateView(rowData),
          },
          canViewDocumentContents && {
            icon: "download",
            position: "row",
            onClick: (event, rowData) => onRowDownloadClick(rowData.id),
          },
          canRemoveDocument && {
            icon: "remove",
            position: "row",
            onClick: (event, rowData) => removeDocument(rowData.id),
          },
        ]}
        components={{
          Actions: (props) => <CustomMTableActions {...props} />,
          Action: (props) => {
            if (typeof props.action === "function") {
              var element = props.action(props.data);
              return (
                <Tooltip
                  title={t(
                    "labels.record.documents.table.actions.editFileName",
                  )}
                  placement="bottom"
                >
                  <IconButton
                    onClick={(event) => element.onClick(event, props.data)}
                    style={{ fontSize: 16 }}
                  >
                    <Icon className="far fa-pen-alt" />
                  </IconButton>
                </Tooltip>
              );
            } else {
              switch (props.action.icon) {
                case "view":
                  return props.data.mime_type.toLowerCase() ===
                    "application/pdf" ||
                    props.data.mime_type.toLowerCase().startsWith("image/") ? (
                    <Tooltip
                      title={t("labels.record.documents.table.actions.view")}
                      placement="bottom"
                    >
                      <span>
                        <IconButton
                          onClick={(event) =>
                            props.action.onClick(event, props.data)
                          }
                          disabled={props.data.azure_blob_name ? false : true}
                          style={{ fontSize: 16 }}
                        >
                          <Icon className="far fa-file-alt" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null;
                case "download":
                  return (
                    <Tooltip
                      title={t(
                        "labels.record.documents.table.actions.download",
                      )}
                      placement="bottom"
                    >
                      <span>
                        <IconButton
                          onClick={(event) =>
                            props.action.onClick(event, props.data)
                          }
                          disabled={props.data.azure_blob_name ? false : true}
                          style={{ fontSize: 16 }}
                        >
                          <Icon className="far fa-cloud-download" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                case "remove":
                  return (
                    <Tooltip
                      title={t("labels.record.documents.table.actions.remove")}
                      placement="bottom"
                    >
                      <IconButton
                        onClick={(event) =>
                          props.action.onClick(event, props.data)
                        }
                        style={{ fontSize: 16 }}
                      >
                        <Icon className="far fa-trash-alt" />
                      </IconButton>
                    </Tooltip>
                  );
                default:
                  return (
                    <IconButton
                      aria-label={props.action.icon}
                      size="small"
                      onClick={props.action.onClick}
                    >
                      {props.action.icon.render(props)}
                    </IconButton>
                  );
              }
            }
          },
        }}
      />
    </div>
  );
};

export default DocumentsTable;
